import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiInvalidToken } from '@biz-action/system-actions/auth-token.actions';
import { ValidationError } from '@biz-action/system-actions/general.actions';
import { AuthenticationToken } from '@biz-selectors/app.selectors';
import { userToken } from '@biz-store/auth/feature.selectors';

import { MICRO_SERVICES, MICRO_SERVICES_ENDPOINT } from '@env/environment';
import { Store } from '@ngrx/store';
import { catchError, combineLatest, filter, map, Observable, of, switchMap, take } from 'rxjs';

export type MicroService = (typeof MICRO_SERVICES)[number];
type HttpGetOptions = Parameters<HttpClient['get']>[1];

@Injectable({
	providedIn: 'root',
})
export class ApiService {
	constructor(private http: HttpClient, private store: Store) {}

	get$<T>(microService: MicroService, url: string, options?: HttpGetOptions) {
		return this._getHttpOptions$(options).pipe(
			switchMap((customOptions) => this.http.get<T>(this.getApiUrl(microService) + `/${url}`, customOptions)),
			catchError((err: HttpErrorResponse, caught) => {
				this._handleError(err);
				throw new Error(err.error.message);
			}),
		);
	}

	post$<T>(microService: MicroService, url: string, body: any, options?: HttpGetOptions) {
		return this._getHttpOptions$(options).pipe(
			switchMap((customOptions) => {
				return this.http.post<T>(this.getApiUrl(microService) + `/${url}`, body, customOptions);
			}),
			catchError((err: HttpErrorResponse, caught) => {
				this._handleError(err);
				throw new Error(err.error.message);
			}),
		);
	}

	postWithoutToken$<T>(microService: MicroService, url: string, body: any, options?: HttpGetOptions) {
		return this._getHttpOptionsLogin$(options).pipe(
			switchMap((customOptions) => {
				return this.http.post<T>(this.getApiUrl(microService) + `/${url}`, body, customOptions);
			}),
			catchError((err: HttpErrorResponse, caught) => {
				this._handleError(err);
				throw new Error(err.error.message);
			}),
		);
	}

	put$<T>(microService: MicroService, url: string, body: any, options?: HttpGetOptions) {
		return this._getHttpOptions$(options).pipe(
			switchMap((customOptions) =>
				this.http.put<T>(this.getApiUrl(microService) + `/${url}`, body, customOptions),
			),
			catchError((err: HttpErrorResponse, caught) => {
				this._handleError(err);
				throw new Error(err.error.message);
			}),
		);
	}

	patch$<T>(microService: MicroService, url: string, body: any, options?: HttpGetOptions) {
		return this._getHttpOptions$(options).pipe(
			switchMap((customOptions) =>
				this.http.patch<T>(this.getApiUrl(microService) + `/${url}`, body, customOptions),
			),
			catchError((err: HttpErrorResponse, caught) => {
				this._handleError(err);
				throw new Error(err.error.message);
			}),
		);
	}

	delete$<T>(microService: MicroService, url: string, options?: HttpGetOptions) {
		return this._getHttpOptions$(options).pipe(
			switchMap((customOptions) =>
				this.http.delete<T>(this.getApiUrl(microService) + `/${url}`, customOptions),
			),
			catchError((err: HttpErrorResponse, caught) => {
				this._handleError(err);
				throw new Error(err.error.message);
			}),
		);
	}

	private _handleError(err: HttpErrorResponse) {
		if (err.status === 401) this.store.dispatch(ApiInvalidToken());
		if (err.status === 422) this.store.dispatch(ValidationError(err.error.data));
	}

	private _getHttpOptionsLogin$(options: HttpGetOptions = {}): Observable<HttpGetOptions> {
		//this.store.select(userToken)
		return combineLatest([of(''), of('')]).pipe(
			map(([subdomain, token]) => {
				return {
					...options,
					headers: {
						authorization: `Bearer ${token !== undefined ? token : undefined}`,
						...options.headers,
					},
					params: {
						...(!!localStorage.getItem('subdomain') && { _subdomain: localStorage.getItem('subdomain') }),
						...options.params,
					},
				};
			}),
		);
	}

	private _getHttpOptions$(options: HttpGetOptions = {}): Observable<HttpGetOptions> {
		return combineLatest([of(''), of('')]).pipe(
			map(([subdomain, token]) => {
				return {
					...options,
					headers: {
						authorization: `Bearer ${localStorage.getItem('token')}`,
						...(!!localStorage.getItem('subdomain') && { 'x-subdomain': localStorage.getItem('subdomain') }),
						...options.headers,
					},
					params: {
						...(!!localStorage.getItem('subdomain') && { _subdomain: localStorage.getItem('subdomain') }),
						...options.params,
					},
				};
			}),
		);
	}

	getApiUrl(microService: MicroService): string {
		return MICRO_SERVICES_ENDPOINT[MICRO_SERVICES.indexOf(microService)] + `/api/${microService}`;
	}
}
